import { PromptResolve } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Input, NumberInput, RegularModal } from 'components'
import { DetectBlur } from 'containers/util-containers'
import { AbsenceTypeNode } from 'modules/timesheets/types.graphql'
import { Field, Form } from '..'
import { Color, SettingsColorPicker } from '../common'
import styled from 'styled-components'

const SpecialLabel = styled.label`
  border-left: 3px solid ${props => props.theme.colors.primary};
  padding-left: 0.5rem;
  margin-top: 0.5rem !important;
`

export interface AbsenceTypeForm {
  externalId: string | null
  name: string
  color: string
  defaultDayValue: number
}

interface AbsenceTypePromptProps {
  initialData?: AbsenceTypeForm
  existingAbsenceTypes: Pick<AbsenceTypeNode, 'name' | 'id'>[]
  onSubmit: PromptResolve<AbsenceTypeForm | null>
}

export const AbsenceTypePrompt: React.FC<AbsenceTypePromptProps> = ({
  initialData,
  existingAbsenceTypes,
  onSubmit,
}) => {
  const translations = useTranslate({
    form: {
      label: {
        name: 'common.name',
        color: 'common.color',
        id: 'common.id',
        defaultDayValue: 'settings.default-day-value',
      },
      placeholder: {
        id: 'settings.external-id-in-salary-system',
        defaultDayValue: 'settings.default-day-value-placeholder',
      },
    },

    addAbsenceType: 'settings.add-absence-type',
    editAbsenceType: 'settings.edit-absence-type',

    create: 'common.create-alt',
    update: 'common.update',

    validation: {
      nameExists: 'settings.validation.absence-type-name-exists',
      required: 'common.required',
    },
  })

  const editing = typeof initialData !== 'undefined'

  function validateName(name: string, formName: string) {
    if (name === initialData?.name) return null
    if (name === '') return translations.validation.required
    if (!!existingAbsenceTypes.find(type => type.name === formName)) {
      return translations.validation.nameExists
    }
    return null
  }

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    formChangeHandler: handler,
    submitHandler: submit,
  } = useForm<AbsenceTypeForm>({
    values: initialData ?? {
      externalId: '',
      name: '',
      color: 'hsl(138deg, 80%, 80%)',
      defaultDayValue: 1,
    },
    validators: {
      name: (val, form) => validateName(val, form.name),
    },
  })

  return (
    <DetectBlur active onClickOutside={() => onSubmit(null)}>
      <RegularModal
        title={
          editing ? translations.editAbsenceType : translations.addAbsenceType
        }
        submitText={editing ? translations.update : translations.create}
        submitDisabled={!formValid}
        width="400px"
        onCancel={() => onSubmit(null)}
        onSubmit={submit(onSubmit)}
      >
        <Form>
          <Field>
            <label>{translations.form.label.name}</label>
            <Input
              value={form.name}
              error={errors.name}
              autoFocus
              fullWidth
              onChange={handler('name')}
            />
          </Field>
          <Field>
            <label>{translations.form.label.color}</label>
            <Color color={form.color}>
              <SettingsColorPicker
                value={form.color}
                onChange={handler('color')}
              />
            </Color>
          </Field>
          <h4>Verdier mot lønnssystem</h4>
          <Field>
            <label>{translations.form.label.id}</label>
            <Input
              value={form.externalId ?? ''}
              fullWidth
              onChange={handler('externalId')}
              placeholder={translations.form.placeholder.id}
            />
          </Field>
          <Field>
            <label>{translations.form.label.defaultDayValue}</label>
            <NumberInput
              value={form.defaultDayValue}
              fullWidth
              onChange={handler('defaultDayValue')}
              min={0}
            />
            <SpecialLabel>
              {translations.form.placeholder.defaultDayValue}
            </SpecialLabel>
          </Field>
        </Form>
      </RegularModal>
    </DetectBlur>
  )
}
